import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Divider, ExternalLinkOutline, HStack, Link, Popover, PopoverContent, PopoverTrigger, Portal, Text, VStack } from '@bonusly/design-system';

import { useIsTouchscreen } from '../../../hooks/use-is-touchscreen';
import type { BizyMomentReference, BizyPeopleSource } from '../../../lib/bizy/types';
import { formatShortDateTime } from '../../../utils/date';
import { BizySourceIcon } from './bizy-source-icon';

// Popover linking out to a moment's cited sources (BIZY-744): a Bonusly post, a
// meeting, a Slack message, a GitHub PR, or an MS Teams message. A moment can
// carry several references under the same connector (e.g. a mutual-recognition
// moment citing 6 recognition posts) — the trigger stays a single `BizySourceIcon`
// keyed off `moment.source` (no per-reference source field, deliberately), with a
// small count badge when there's more than one, and the popover lists every
// reference as its own linked row.

// Reference types the backend is known to send today, each with tailored
// fallback copy. An unrecognized type (future connector, etc.) falls back to
// a generic sentence-cased rendering of the raw type, mirroring the backend's
// own `reference_type.titleize` fallback in deliver_notification_task.rb.
const KNOWN_REFERENCE_TYPE_KEYS = ['recognition', 'github_pull_request', 'slack_message', 'ms_teams_message', 'meeting'] as const;
type KnownReferenceType = (typeof KNOWN_REFERENCE_TYPE_KEYS)[number];

function isKnownReferenceType(referenceType: string): referenceType is KnownReferenceType {
	return (KNOWN_REFERENCE_TYPE_KEYS as readonly string[]).includes(referenceType);
}

function sentenceCase(rawType: string): string {
	const words = rawType.split(/[_\s]+/).filter(Boolean);
	if (words.length === 0) return '';
	return [words[0].charAt(0).toUpperCase() + words[0].slice(1), ...words.slice(1)].join(' ');
}

// A reference with a confirmed URL — the only shape this component ever renders.
type LinkableReference = BizyMomentReference & { referenceUrl: string };

function isLinkable(reference: BizyMomentReference): reference is LinkableReference {
	return Boolean(reference.referenceUrl);
}

// Each row falls back independently (old- and new-shape references can coexist
// on one moment if it was updated over time), so this is computed per reference
// rather than once for the whole list.
function referenceTitle(t: (key: string) => string, reference: LinkableReference): string {
	if (reference.title) return reference.title;

	const referenceType = reference.referenceType ?? undefined;
	if (!referenceType) return t('personView.sourceReference.fallbackTitles.default');
	if (isKnownReferenceType(referenceType)) return t(`personView.sourceReference.fallbackTitles.${referenceType}`);
	return sentenceCase(referenceType);
}

export interface BizySourceReferencePopoverProps {
	source: BizyPeopleSource | null | undefined;
	references?: BizyMomentReference[] | null;
	size?: string;
	// When true, a moment citing more than one source shows a small "+N" count
	// badge on the trigger icon. The previous-memories list opts out to keep its
	// rows visually quiet; the active moment card keeps it on.
	showCount?: boolean;
}

/**
 * Wraps `BizySourceIcon` in a popover listing every reference a moment cites,
 * each linking out to its source. Falls back to the plain, non-clickable icon
 * when the moment has no linkable reference.
 */
export function BizySourceReferencePopover({ source, references, size = '24px', showCount = true }: BizySourceReferencePopoverProps) {
	const { t } = useTranslation('admin_bizy');
	const isTouchscreen = useIsTouchscreen();

	const linkableReferences = (references ?? []).filter(isLinkable);

	if (linkableReferences.length === 0) {
		return <BizySourceIcon source={source} size={size} />;
	}

	const hasMultiple = linkableReferences.length > 1;
	const showBadge = showCount && hasMultiple;
	const triggerLabel = hasMultiple
		? t('personView.sourceReference.ariaLabelCount', { count: linkableReferences.length })
		: referenceTitle(t, linkableReferences[0]);

	// Badge scales with the trigger icon rather than a fixed size.
	const iconSize = parseInt(size, 10);
	const badgeMinSize = `${Math.max(12, Math.round(iconSize * 0.6))}px`;
	const badgeFontSize = `${Math.max(7, Math.round(iconSize * 0.35))}px`;

	return (
		<Popover isLazy trigger={isTouchscreen ? 'click' : 'hover'} placement="bottom-start">
			<PopoverTrigger>
				{/* Some call sites (e.g. the previous-memories accordion rows) nest this
				    trigger inside another clickable element; stop the click here so tapping
				    the icon opens the popover instead of also toggling the ancestor.

				    The slot is exactly the icon's size: the count badge is absolutely
				    positioned (out of flow), so it never widens the trigger, and every
				    row's icon keeps the same layout width — no reserved gap before the
				    timestamp that follows it. */}
				<Box
					as="span"
					display="inline-flex"
					alignItems="center"
					w={size}
					h={size}
					flexShrink={0}
					cursor="pointer"
					aria-label={triggerLabel}
					onClick={(event: MouseEvent) => event.stopPropagation()}
				>
					<Box position="relative" display="inline-flex" flexShrink={0}>
						<BizySourceIcon source={source} size={size} />
						{showBadge && (
							<Box
								position="absolute"
								bottom="-2px"
								right="-4px"
								minW={badgeMinSize}
								h={badgeMinSize}
								px="3px"
								borderRadius="full"
								bg="bg.subtle"
								color="text.default"
								border="1px solid"
								borderColor="bg.surface"
								display="flex"
								alignItems="center"
								justifyContent="center"
								fontSize={badgeFontSize}
								fontWeight="semibold"
								lineHeight="1"
							>
								+{linkableReferences.length}
							</Box>
						)}
					</Box>
				</Box>
			</PopoverTrigger>
			<Portal>
				<Box
					sx={{
						'& .chakra-popover__popper': {
							zIndex: 'popover',
						},
					}}
				>
					<PopoverContent w="auto" maxH="320px" overflowY="auto" borderColor="bg.subtle" boxShadow="xl" p={0}>
						<VStack align="stretch" spacing={0} divider={<Divider />}>
							{linkableReferences.map((reference, index) => {
								const title = referenceTitle(t, reference);
								// The connector name is intentionally omitted: every row already
								// carries the connector's icon and each popover is single-source,
								// so the subtitle is just the event's date (when we have it).
								const subtitle = reference.occurredAt ? formatShortDateTime(reference.occurredAt) : undefined;

								return (
									<Link key={`${reference.referenceUrl}-${index}`} href={reference.referenceUrl} isExternal display="block" _hover={{ textDecoration: 'none' }}>
										<HStack spacing={2} p={3} align="center">
											<BizySourceIcon source={source} size="24px" />
											<VStack align="flex-start" spacing={0} minW={0}>
												<Text fontSize="sm" fontWeight="semibold" color="text.default" noOfLines={1}>
													{title}
												</Text>
												{subtitle && (
													<Text fontSize="xs" color="text.softest" noOfLines={1}>
														{subtitle}
													</Text>
												)}
											</VStack>
											<ExternalLinkOutline boxSize={4} color="text.softest" flexShrink={0} />
										</HStack>
									</Link>
								);
							})}
						</VStack>
					</PopoverContent>
				</Box>
			</Portal>
		</Popover>
	);
}
